const RETURN_TO_KEY = 'aibuild_return_to';
const SSO_STATE_KEY = 'aibuild_sso_state';
const RETURN_TO_TTL_MS = 60 * 60 * 1000;

export const RETURN_TO_PARAM = 'returnTo';

function allowedHosts(): string[] {
  const hosts = [globalThis.location.host];
  const camUrl = import.meta.env.VITE_CAM_URL;
  if (camUrl) {
    try {
      hosts.push(new URL(camUrl).host);
    } catch {
      // A malformed VITE_CAM_URL just means CAM is not an allowed target.
    }
  }
  return hosts;
}

/** Exact-host allowlist (this app and CAM), so a crafted link can't bounce a fresh session elsewhere. */
export function safeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw, globalThis.location.origin);
  } catch {
    return null;
  }
  const secure = url.protocol === 'https:' || (url.protocol === 'http:' && url.hostname === 'localhost');
  return secure && allowedHosts().includes(url.host) ? url.href : null;
}

// localStorage rather than sessionStorage: the email that verifies a new
// account opens a new tab, and the way back has to survive it.
export function rememberReturnTo(raw: string | null): void {
  const target = safeReturnTo(raw);
  if (target) {
    localStorage.setItem(RETURN_TO_KEY, JSON.stringify({ target, expiresAt: Date.now() + RETURN_TO_TTL_MS }));
  }
}

export function consumeReturnTo(): string | null {
  const stored = localStorage.getItem(RETURN_TO_KEY);
  forgetReturnTo();
  if (!stored) return null;
  try {
    const { target, expiresAt } = JSON.parse(stored) as { target?: unknown; expiresAt?: unknown };
    const live = typeof expiresAt === 'number' && expiresAt > Date.now();
    return live && typeof target === 'string' ? safeReturnTo(target) : null;
  } catch {
    return null;
  }
}

export function forgetReturnTo(): void {
  localStorage.removeItem(RETURN_TO_KEY);
}

export function savePendingSsoState(state: string): void {
  sessionStorage.setItem(SSO_STATE_KEY, state);
}

export function takePendingSsoState(): string | null {
  const state = sessionStorage.getItem(SSO_STATE_KEY);
  sessionStorage.removeItem(SSO_STATE_KEY);
  return state;
}

export function authPathWithReturnTo(page: 'signup' | 'login', returnTo: string): string {
  return `/auth/${page}?${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}`;
}

export function ssoLoginPath(provider: string): string {
  return `/sso/${encodeURIComponent(provider)}`;
}
